import { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { Button, Icons, ProjectAvatar, TextInput } from "@/components/ui";
import { confirmDialogPanelVariants } from "@/components/ui/confirm-dialog";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import { isTauri } from "@/lib/is-tauri";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";

interface FrameMoveToProjectDialogProps {
  /** One frame, or a wall selection. */
  frameIds: readonly string[];
  /** The project the frames are in: the one on screen unless named. */
  fromProjectId?: string;
  onMove: (projectId: string) => Promise<boolean>;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function FrameMoveToProjectDialog({
  frameIds,
  fromProjectId,
  onMove,
  onClose,
  returnFocusRef,
}: FrameMoveToProjectDialogProps) {
  const t = useT(framesSharedMessages);
  const projects = useProjectStore((state) => state.projects);
  const shownProjectId = useProjectStore(
    (state) => state.currentProject?.id ?? null,
  );
  const sourceProjectId = fromProjectId ?? shownProjectId;
  const isLoading = useProjectStore((state) => state.isLoading);
  const fetchProjects = useProjectStore((state) => state.fetchAllProjects);
  const createProject = useProjectStore(
    (state) => state.createProjectWithDefaults,
  );
  const [creating, setCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [movingProjectId, setMovingProjectId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const fallbackOpenerRef = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  const focusedRef = useRef(false);
  const modalLayer = useInteractiveLayer("frame-move-project-modal", {
    baseZIndex: 70,
    tier: "modal",
  });
  const desktop = isTauri();

  useEffect(() => {
    if (projects.length === 0) void fetchProjects();
  }, [fetchProjects, projects.length]);

  useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (!focusedRef.current) {
      focusedRef.current = true;
      requestAnimationFrame(() => {
        const firstControl = panel.querySelector<HTMLElement>(
          "button:not(:disabled), input:not(:disabled)",
        );
        (firstControl ?? panel).focus();
      });
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !movingProjectId) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = Array.from(
        panel.querySelectorAll<HTMLElement>(
          "button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex='-1'])",
        ),
      );
      if (controls.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = controls[0]!;
      const last = controls[controls.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, movingProjectId]);

  useEffect(
    () => () => {
      (returnFocusRef?.current ?? fallbackOpenerRef.current)?.focus();
    },
    [returnFocusRef],
  );

  async function move(project: (typeof projects)[number]) {
    if (movingProjectId || project.id === sourceProjectId) return;
    setMovingProjectId(project.id);
    setError(null);
    try {
      const moved = await onMove(project.id);
      if (!moved) {
        setMovingProjectId(null);
        return;
      }
      useNotificationStore.getState().addSystemToast({
        id: `frame-moved-${frameIds.join(",")}-${project.id}`,
        title: t("frameMoved", { count: frameIds.length }),
        description: t("movedTo", { project: project.name }),
        level: "info",
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("moveFrameFailed"));
      setMovingProjectId(null);
    }
  }

  async function createAndMove() {
    const name = newProjectName.trim();
    if (!name || movingProjectId) return;
    setMovingProjectId("new");
    setError(null);
    let project: Awaited<ReturnType<typeof createProject>>;
    try {
      project = await createProject(name);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : t("createProjectFailed"),
      );
      setMovingProjectId(null);
      return;
    }
    setCreating(false);
    setNewProjectName("");
    setMovingProjectId(null);
    await move(project);
  }

  return createPortal(
    <>
      {!desktop && (
        <div
          className="fixed inset-0 bg-black/30"
          style={{ zIndex: modalLayer.zIndex }}
          onClick={() => !movingProjectId && onClose()}
          data-testid="frame-move-project-dialog-backdrop"
          aria-hidden="true"
        />
      )}
      <div
        className="pointer-events-none fixed inset-0 flex items-center justify-center"
        style={{ zIndex: modalLayer.zIndex + 1 }}
        data-testid="frame-move-project-dialog-positioner"
      >
        <section
          ref={panelRef}
          tabIndex={-1}
          className={cn(
            surfaceRoleClassName("menu"),
            confirmDialogPanelVariants(),
            "pointer-events-auto w-[22rem]",
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="frame-move-project-dialog-title"
          data-testid="frame-move-project-dialog"
          data-interactive="true"
          {...modalLayer.interactionProps}
          {...surfaceRoleAttributes("menu", {
            nativeLevel: "root",
            nativeGroup: "menus",
          })}
        >
          <header className="flex items-center justify-between border-b border-glass-stroke-faint px-5 py-3">
            <div className="flex items-center gap-2">
              <Icons.FolderKanban className="h-4 w-4 text-accent" />
              <h2
                id="frame-move-project-dialog-title"
                className="text-appkit-body font-semibold text-primary"
              >
                {t("moveToProject")}
              </h2>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onClose}
              disabled={Boolean(movingProjectId)}
              aria-label={t("closeProjectPicker")}
              data-testid="frame-move-project-dialog-close"
            >
              <Icons.X className="h-3.5 w-3.5" />
            </Button>
          </header>

          <div className="scrollbar-none max-h-72 overflow-y-auto p-2">
            {isLoading && projects.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-8 text-appkit-caption text-muted">
                <Icons.Spinner className="h-4 w-4 animate-spin" />
                {t("loadingProjects")}
              </div>
            ) : (
              projects.map((project) => {
                const moving = movingProjectId === project.id;
                const current = project.id === sourceProjectId;
                return (
                  <Button
                    key={project.id}
                    type="button"
                    variant="ghost"
                    className="min-h-11 w-full justify-start gap-3 rounded-control px-3 text-left"
                    onClick={() => void move(project)}
                    disabled={Boolean(movingProjectId) || current}
                    data-testid={`frame-move-project-option-${project.id}`}
                  >
                    <ProjectAvatar
                      name={project.name}
                      color={project.color || "#30D158"}
                      isHome={Boolean(project.is_home)}
                      size="sm"
                      data-testid={`frame-move-project-avatar-${project.id}`}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {project.name}
                    </span>
                    {moving ? (
                      <Icons.Spinner className="h-4 w-4 animate-spin text-accent" />
                    ) : current ? (
                      <span className="shrink-0 text-appkit-mini text-muted">
                        {t("currentProject")}
                      </span>
                    ) : (
                      <Icons.ChevronRight className="h-4 w-4 text-muted" />
                    )}
                  </Button>
                );
              })
            )}
          </div>

          <div className="border-t border-glass-stroke-faint p-3">
            {creating ? (
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  void createAndMove();
                }}
              >
                <TextInput
                  ref={inputRef}
                  value={newProjectName}
                  onChange={(event) => setNewProjectName(event.target.value)}
                  maxLength={255}
                  placeholder={t("projectNamePlaceholder")}
                  aria-label={t("newProjectName")}
                  disabled={Boolean(movingProjectId)}
                  data-testid="frame-move-project-new-name"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!newProjectName.trim() || Boolean(movingProjectId)}
                  data-testid="frame-move-project-create-confirm"
                >
                  {movingProjectId === "new" ? (
                    <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t("create")
                  )}
                </Button>
              </form>
            ) : (
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => setCreating(true)}
                disabled={Boolean(movingProjectId)}
                data-testid="frame-move-project-create"
              >
                <Icons.Plus className="h-4 w-4" />
                {t("newProject")}
              </Button>
            )}
            {error ? (
              <p className="mt-2 text-appkit-caption text-danger" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </>,
    document.body,
  );
}
